import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ClipboardList, CheckCircle2, XCircle, Trophy, Play, RotateCcw } from "lucide-react";
import { useModuleQuizzes, useQuizWithQuestions, Quiz } from "@/hooks/useQuizzes";

interface QuizSectionProps {
  moduleId: string;
}

interface QuizAttempt {
  id: string;
  quiz_id: string;
  user_id: string;
  score: number | null;
  max_score: number | null;
  percentage: number | null;
  passed: boolean | null;
  started_at: string;
  completed_at: string | null;
}

export function QuizSection({ moduleId }: QuizSectionProps) {
  const { data: quizzes, isLoading } = useModuleQuizzes(moduleId);
  const publishedQuizzes = quizzes?.filter(q => q.is_published) || [];

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-muted-foreground">Loading quizzes...</p>
        </CardContent>
      </Card>
    );
  }

  if (publishedQuizzes.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold flex items-center gap-2">
        <ClipboardList className="h-5 w-5" />
        Quizzes
      </h3>
      {publishedQuizzes.map((quiz) => (
        <QuizCard key={quiz.id} quiz={quiz} />
      ))}
    </div>
  );
}

function QuizCard({ quiz }: { quiz: Quiz }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: attempts } = useQuery({
    queryKey: ["quiz-attempts", quiz.id, user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from("quiz_attempts")
        .select("*")
        .eq("quiz_id", quiz.id)
        .eq("user_id", user.id)
        .order("started_at", { ascending: false });
      if (error) throw error;
      return data as QuizAttempt[];
    },
    enabled: !!user,
  });

  const lastAttempt = attempts?.[0];
  const bestAttempt = attempts?.reduce((best, current) => {
    if (!best || (current.percentage || 0) > (best.percentage || 0)) return current;
    return best;
  }, null as QuizAttempt | null);

  const hasPassed = bestAttempt?.passed;

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <CardTitle className="text-base flex items-center gap-2">
              {quiz.title}
              {hasPassed && (
                <Badge variant="default" className="bg-green-600">
                  <CheckCircle2 className="h-3 w-3 mr-1" />
                  Passed
                </Badge>
              )}
            </CardTitle>
            {quiz.description && (
              <p className="text-sm text-muted-foreground">{quiz.description}</p>
            )}
          </div>
          <Button
            variant={isExpanded ? "secondary" : "default"}
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? "Close" : hasPassed ? (
              <>
                <RotateCcw className="h-4 w-4 mr-2" />
                Retake
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Start Quiz
              </>
            )}
          </Button>
        </div>
        <div className="flex gap-4 text-sm text-muted-foreground mt-2">
          <span>Passing score: {quiz.passing_score}%</span>
          {quiz.time_limit_minutes && (
            <span>Time limit: {quiz.time_limit_minutes} min</span>
          )}
          {attempts && attempts.length > 0 && (
            <span>Attempts: {attempts.length}</span>
          )}
        </div>
        {bestAttempt && (
          <div className="mt-2 flex items-center gap-2 text-sm">
            <Trophy className="h-4 w-4 text-yellow-500" />
            <span>Best score: {bestAttempt.percentage}%</span>
          </div>
        )}
      </CardHeader>
      {isExpanded && (
        <CardContent>
          <QuizTaker
            quizId={quiz.id}
            passingScore={quiz.passing_score}
            onComplete={() => {
              queryClient.invalidateQueries({ queryKey: ["quiz-attempts", quiz.id] });
              queryClient.invalidateQueries({ queryKey: ["module-requirements"] });
            }}
          />
        </CardContent>
      )}
    </Card>
  );
}

interface QuizTakerProps {
  quizId: string;
  passingScore: number;
  onComplete: () => void;
}

function QuizTaker({ quizId, passingScore, onComplete }: QuizTakerProps) {
  const { toast } = useToast();
  const { user } = useAuth();
  const { data: quiz, isLoading } = useQuizWithQuestions(quizId);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [attemptId, setAttemptId] = useState<string | null>(null);
  const [result, setResult] = useState<{
    score: number;
    maxScore: number;
    percentage: number;
    passed: boolean;
  } | null>(null);

  const startAttempt = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error("Must be logged in");
      const { data, error } = await supabase
        .from("quiz_attempts")
        .insert({
          quiz_id: quizId,
          user_id: user.id,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      setAttemptId(data.id);
    },
  });

  const submitQuiz = useMutation({
    mutationFn: async () => {
      if (!attemptId || !quiz?.questions) throw new Error("Quiz not started");

      let score = 0;
      let maxScore = 0;

      // Calculate score and save answers
      for (const question of quiz.questions) {
        maxScore += question.points;
        const selectedOptionId = answers[question.id];
        const selectedOption = question.options.find(o => o.id === selectedOptionId);
        const isCorrect = selectedOption?.is_correct || false;
        const pointsEarned = isCorrect ? question.points : 0;
        score += pointsEarned;

        // Save answer
        await supabase.from("quiz_answers").insert({
          attempt_id: attemptId,
          question_id: question.id,
          selected_option_id: selectedOptionId || null,
          is_correct: isCorrect,
          points_earned: pointsEarned,
        });
      }

      const percentage = Math.round((score / maxScore) * 100);
      const passed = percentage >= passingScore;

      // Update attempt with results
      const { error } = await supabase
        .from("quiz_attempts")
        .update({
          score,
          max_score: maxScore,
          percentage,
          passed,
          completed_at: new Date().toISOString(),
        })
        .eq("id", attemptId);

      if (error) throw error;

      return { score, maxScore, percentage, passed };
    },
    onSuccess: (data) => {
      setResult(data);
      onComplete();
      toast({
        title: data.passed ? "Congratulations!" : "Quiz Completed",
        description: data.passed
          ? `You passed with ${data.percentage}%!`
          : `You scored ${data.percentage}%. Keep studying and try again!`,
      });
    },
  });

  if (isLoading) {
    return <p className="text-muted-foreground">Loading quiz...</p>;
  }

  if (!quiz?.questions || quiz.questions.length === 0) {
    return <p className="text-muted-foreground">This quiz has no questions yet.</p>;
  }

  // Show result screen
  if (result) {
    return (
      <div className="text-center py-6 space-y-4">
        <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full ${result.passed ? 'bg-green-100' : 'bg-red-100'}`}>
          {result.passed ? (
            <CheckCircle2 className="h-10 w-10 text-green-600" />
          ) : (
            <XCircle className="h-10 w-10 text-red-600" />
          )}
        </div>
        <h3 className="text-xl font-semibold">
          {result.passed ? "You Passed!" : "Not Quite..."}
        </h3>
        <p className="text-muted-foreground">
          You scored {result.score} out of {result.maxScore} points ({result.percentage}%)
        </p>
        <p className="text-sm text-muted-foreground">
          Passing score: {passingScore}%
        </p>
      </div>
    );
  }

  // Start quiz
  if (!attemptId) {
    return (
      <div className="text-center py-6 space-y-4">
        <p className="text-muted-foreground">
          This quiz has {quiz.questions.length} questions.
        </p>
        <Button onClick={() => startAttempt.mutate()} disabled={startAttempt.isPending}>
          {startAttempt.isPending ? "Starting..." : "Begin Quiz"}
        </Button>
      </div>
    );
  }

  const question = quiz.questions[currentQuestion];
  const progress = ((currentQuestion + 1) / quiz.questions.length) * 100;
  const allAnswered = quiz.questions.every(q => answers[q.id]);

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex justify-between text-sm text-muted-foreground">
          <span>Question {currentQuestion + 1} of {quiz.questions.length}</span>
          <span>{question.points} point{question.points !== 1 ? 's' : ''}</span>
        </div>
        <Progress value={progress} className="h-2" />
      </div>

      <div className="space-y-4">
        <h4 className="font-medium">{question.question_text}</h4>
        <RadioGroup
          value={answers[question.id] || ""}
          onValueChange={(value) => setAnswers({ ...answers, [question.id]: value })}
        >
          {question.options.map((option) => (
            <div key={option.id} className="flex items-center space-x-2">
              <RadioGroupItem value={option.id} id={option.id} />
              <Label htmlFor={option.id} className="cursor-pointer flex-1">
                {option.option_text}
              </Label>
            </div>
          ))}
        </RadioGroup>
      </div>

      <div className="flex justify-between pt-4">
        <Button
          variant="outline"
          onClick={() => setCurrentQuestion(c => c - 1)}
          disabled={currentQuestion === 0}
        >
          Previous
        </Button>
        <div className="flex gap-2">
          {currentQuestion < quiz.questions.length - 1 ? (
            <Button
              onClick={() => setCurrentQuestion(c => c + 1)}
              disabled={!answers[question.id]}
            >
              Next
            </Button>
          ) : (
            <Button
              onClick={() => submitQuiz.mutate()}
              disabled={!allAnswered || submitQuiz.isPending}
            >
              {submitQuiz.isPending ? "Submitting..." : "Submit Quiz"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}